/**
 * One-click mock exam generation runs as a background generation job
 * (job_kind 'oneclick_mock'). It has no workbook: the worker saves the result
 * as a generation draft and reports its id in job.results[0].draft_id.
 */

import type { GenerateRequest, OneClickModelVariant } from '@/services/oneclickApi';

export const ONECLICK_JOB_KIND = 'oneclick_mock';
export const ONECLICK_OCR_JOB_KIND = 'oneclick_ocr';
export const ONECLICK_OCR_COMPLETED_EVENT = 'autoqm:oneclick-ocr-completed';
// A missing job has unknown outcome; refresh saved data without opening a
// success dialog or navigating to a possibly nonexistent pending OCR result.
export const ONECLICK_JOB_REMOVED_EVENT = 'autoqm:oneclick-job-removed';
export interface OneClickJobRemovedDetail {
  jobId: string;
  jobKind: string;
}

export function notifyOneClickJobRemoved(jobId: string, kind: string | null | undefined): void {
  if (!kind || !isOneClickJobKind(kind)) return;
  window.dispatchEvent(new CustomEvent<OneClickJobRemovedDetail>(ONECLICK_JOB_REMOVED_EVENT, {
    detail: { jobId, jobKind: kind },
  }));
}
/** Widget title; a start time tells concurrent one-click jobs apart. */
export function oneclickJobTitle(kind: string | null | undefined, startedAt?: string | number | null): string {
  const title = kind === ONECLICK_OCR_JOB_KIND ? ONECLICK_OCR_JOB_TITLE : ONECLICK_JOB_TITLE;
  if (startedAt == null) return title;
  const at = new Date(startedAt);
  if (Number.isNaN(at.getTime())) return title;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${title} · ${pad(at.getHours())}:${pad(at.getMinutes())} 시작`;
}
export function oneclickCompletedEvent(kind: string | null | undefined): string {
  return kind === ONECLICK_OCR_JOB_KIND ? ONECLICK_OCR_COMPLETED_EVENT : ONECLICK_GENERATION_COMPLETED_EVENT;
}
/** Request fields for a model choice; none for 'default', so a default request body stays unchanged. */
export function modelVariantFields(variant: OneClickModelVariant): Pick<GenerateRequest, 'model_variant' | 'generate_differently'> {
  return variant === 'default' ? {} : { model_variant: variant, generate_differently: true };
}

export const ONECLICK_JOB_TITLE = '원클릭 출제';
/** Progress-widget title of a sample exam reading job (no "OCR" wording on screen). */
export const ONECLICK_OCR_JOB_TITLE = '시험지 읽기';
/** window event fired by the generation context when a oneclick job completes. */
export const ONECLICK_GENERATION_COMPLETED_EVENT = 'autoqm:oneclick-generation-completed';

export interface OneClickGenerationCompletedDetail {
  jobId: string;
  draftId: number | null;
}

export function isOneClickJobKind(kind: string | null | undefined): boolean {
  return kind === ONECLICK_JOB_KIND || kind === ONECLICK_OCR_JOB_KIND;
}

export function oneclickDraftIdFromResults(results: unknown): number | null {
  if (!Array.isArray(results)) return null;
  for (const result of results) {
    const draftId = (result as { draft_id?: unknown } | null)?.draft_id;
    if (typeof draftId === 'number' && Number.isInteger(draftId)) return draftId;
  }
  return null;
}

export const ONECLICK_OCR_PATH = '/one-click-mocktest/ocr';
export const ONECLICK_GENERATE_PATH = '/one-click-mocktest/generate';
/** Step 3: completed and running generations (`?draft=<id>`, `?job=<id>`). */
export const ONECLICK_RESULTS_PATH = '/one-click-mocktest/results';

export function oneclickResultPath(draftId: number, kind?: string | null): string {
  if (kind === ONECLICK_OCR_JOB_KIND) return `${ONECLICK_OCR_PATH}?ocrDraft=${draftId}`;
  return `${ONECLICK_RESULTS_PATH}?draft=${draftId}`;
}

export function oneclickPendingOcrPath(jobId: string): string {
  return `${ONECLICK_OCR_PATH}?ocrJob=${encodeURIComponent(jobId)}`;
}

export function oneclickLiveResultPath(jobId: string): string {
  return `${ONECLICK_RESULTS_PATH}?job=${encodeURIComponent(jobId)}`;
}
